import { AxiosRequestConfig } from "axios";

import type {
  GetCustomerResponseType,
  PostOrderCalcResponseType,
} from "service/apis/CreateOrderapi";

import { moneefloApi } from "./client";

/**
 * Drop-in replacements for the Xano calls the create-order wizard makes, so
 * the wizard itself is untouched.
 *
 * Same contract as the other adapters in this folder: match the signature,
 * match the response shape, change only where the data comes from. Everything
 * here is temporary and goes when the wizard is rewritten against the new API
 * properly.
 *
 * What is not here yet, and why: the document steps need object storage, which
 * has no public domain yet. Those calls return empty rather than invented
 * data, so the step renders its own "nothing on file" state instead of
 * pretending a document exists.
 */

type Payload<T> = AxiosRequestConfig<T>;

/** The quote keys LRS usage off the customer's uuid, not a number. */
const isUuid = (value: unknown): boolean =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const rupees = (minor: string | null | undefined): number =>
  Number(minor ?? 0) / 100;

/* ------------------------------------------------------------------ deals */

/**
 * Deals available to place an order against.
 *
 * The wizard keeps only deals that are `confirmed` and `unutilized`. In the
 * new model that is one thing — an order still at `quoted`, with a rate held
 * and nothing placed against it — so every row returned here carries those
 * two literals to satisfy the filter the screen already applies.
 */
export type LegacyDeal = {
  id: string | number;
  label: string;
  status: string;
  utilization_status: string;
  currency?: string;
  amount?: number;
  contacts_id?: string | number;
  opportunity_id?: string | number;
};

export async function getAllDeals(
  _payload: Payload<unknown>,
): Promise<{ data: LegacyDeal[] }> {
  const { data } = await moneefloApi.get("/orders", {
    params: {
      // A deal is unutilized whether or not its settlement rate has been
      // agreed, and a settled one is exactly the one you want to raise an
      // order against. Asking only for quoted hid every confirmed deal
      // from the picker.
      status: "quoted,confirmed",
      // Forex and generic orders are not remittance deals.
      category: "remittance",
      perPage: 100,
    },
  });
  const items = (data.items ?? []) as {
    id: string;
    orderNo: string;
    currency: string | null;
    fxAmountMinor: string | null;
    customer: { id: string; name: string };
  }[];

  return {
    data: items.map((order) => ({
      // The screen renders `D-${id}`, so this is the order number staff quote
      // rather than the public id. Both address an order on the API.
      id: order.orderNo,
      label: order.orderNo,
      status: "confirmed",
      utilization_status: "unutilized",
      currency: order.currency ?? "",
      amount: rupees(order.fxAmountMinor),
      contacts_id: order.customer.id,
      opportunity_id: undefined,
    })),
  };
}

/* --------------------------------------------------------------- customer */

export type GetCustomerRequestType = Partial<{
  headers: { Authorization: string };
  /** What both screens actually send. */
  pan_no: string;
  q: string;
}>;

/**
 * PAN lookup. Xano matched on the raw number; the new API keeps a keyed hash
 * so the same lookup works without the number being readable at rest.
 */
export async function getCustomer(
  payload: Payload<GetCustomerRequestType> & {
    params?: { pan_no?: string; q?: string };
  },
): Promise<{
  data: GetCustomerResponseType & { pan_adhar_name_mismatch: boolean | null };
}> {
  // Both screens send `pan_no`; only `q` was being read, so every PAN
  // search threw before it reached the API and the screen reported
  // "Something went wrong" over a customer that was there all along.
  const value = (
    payload.params?.pan_no ??
    payload.params?.q ??
    payload.data?.pan_no ??
    payload.data?.q ??
    ""
  ).trim();
  if (!value) throw new Error("A PAN is required to look a customer up.");

  const { data } = await moneefloApi.get("/customers/lookup", {
    // A PAN is stored upper-case; one screen sends it raw.
    params: { docType: "pan", value: value.toUpperCase() },
  });
  const customer = data as {
    id: string;
    fullName: string;
    email: string | null;
    phone: string | null;
    city: string | null;
    beneficiaries?: {
      id: string;
      name: string;
      nickname: string | null;
      relation: string | null;
      country: string | null;
      city: string | null;
      addressLine1: string | null;
      bankName: string | null;
      bankCountry: string | null;
      accountLast4: string | null;
      swiftCode: string | null;
      routingCode: string | null;
      routingCodeType: string | null;
    }[];
  };

  return {
    data: {
      id: customer.id,
      full_name: customer.fullName,
      email: customer.email ?? "",
      phone_number: customer.phone ?? "",
      address_line_1: customer.city ?? "",
      // The picker reads snake_case and was handed the API's camelCase, so
      // every column but the name rendered "N/A". The account number itself
      // stays encrypted -- only the last four ever leave the database, and
      // the last four is what identifies an account in a list.
      beneficiary: (customer.beneficiaries ?? []).map((b) => ({
        ...b,
        beneficiary_name: b.name,
        bank_name: b.bankName ?? "",
        bank_addr: b.addressLine1 ?? b.city ?? "",
        bank_country: b.bankCountry ?? b.country ?? "",
        city: b.city ?? "",
        country: b.country ?? "",
        swift_code: b.swiftCode ?? "",
        account_number: b.accountLast4 ? `••••${b.accountLast4}` : "",
        routing_number: b.routingCode ?? "",
        beneficiaryType: b.relation ?? "",
      })),
      // Xano compared the name on the PAN with the one on the Aadhaar. There
      // is no such check yet, and claiming "no mismatch" would be a claim.
      pan_adhar_name_mismatch: null,
    },
  };
}

/* ------------------------------------------------------------------ price */

export async function postOrderCalc(
  payload: Payload<Record<string, unknown>>,
): Promise<{
  data: PostOrderCalcResponseType & {
    fixed_customer_rate: string;
    at_a_loss: boolean;
    margin: number | null;
  };
}> {
  const body = (payload.data ?? {}) as Record<string, string | number>;

  // An order is priced off the deal it is being raised against, not off
  // today's market: the rate was booked and the customer was told it. The
  // wizard sends the deal's settlement rate and the margins it is showing,
  // and all of it was being dropped here, so the quote came back at the
  // live rate and disagreed with the deal on screen.
  const settlement = Number(body.settlement_rates) || 0;
  const margins =
    Number(body.l1_commission ?? 0) +
    Number(body.l2_commission ?? 0) +
    Number(body.l3_commission ?? 0);
  // `created_by` arrives as a uuid or a number depending on the screen,
  // and as "-1" or 0 for a deal we raised ourselves.
  const text = (value: unknown): string | undefined => {
    const id = value === undefined || value === null ? "" : String(value).trim();
    return id && id !== "-1" && id !== "0" && id !== "NaN" ? id : undefined;
  };
  const num = (value: unknown): number | undefined =>
    value === undefined || value === "" ? undefined : Number(value);

  const { data } = await moneefloApi.post("/orders/quote", {
    // What the customer has already remitted this year is what decides the
    // TCS band, so leaving it out quotes a repeat remitter as if this were
    // their first rupee and under-collects the tax.
    customerId: isUuid(body.customers_id) ? String(body.customers_id) : undefined,
    purposeId: body.purpose_id ? String(body.purpose_id) : undefined,
    currency: body.currency,
    fxAmount: Number(body.amount),
    servicePartnerId: body.service_partner_id
      ? String(body.service_partner_id)
      : undefined,
    settlementRate: settlement > 0 ? settlement : undefined,
    // Settlement plus the margins the screen is showing is the rate the
    // customer is being quoted, so the quote is priced straight to it.
    customerRate: settlement > 0 ? settlement + margins : undefined,
    // Who the deal was raised for, and what they earn on it. Without
    // these the customer rate moved when the desk changed a partner's
    // margin but the partner was still owed their rate card: the
    // difference quietly became ours. With them the three agree — the
    // rate is pinned, the partner's share is what the screen shows, and
    // our own add-on is whatever is left.
    originOrgId: text(body.created_by),
    partnerMargin: num(body.l2_commission),
    addOnRate:
      settlement > 0 || !body.add_on_rates
        ? undefined
        : Number(body.add_on_rates),
    ttCharge: num(body.tt_charges),
    foreignBankCharge: num(body.foreign_bank_charges),
    discount: body.discount ? Number(body.discount) : undefined,
    fxCharges: body.fx_charges === "OUR" ? "OUR" : "BEN",
  });

  const quote = data as {
    customerRate: string;
    baseAmountMinor: string;
    totalPayableMinor: string;
    settlementRate?: string;
    ttChargeMinor: string;
    foreignBankChargeMinor: string;
    lines: { type: string; amountMinor: string; taxMinor: string }[];
    commissions: { partnerLevel: number; amountMinor: string }[];
    atALoss: boolean;
    l1NetMinor?: string;
  };
  const line = (type: string) => quote.lines.find((l) => l.type === type);
  const at = (level: number) =>
    rupees(
      quote.commissions.find((c) => c.partnerLevel === level)?.amountMinor,
    );

  const money = (n: number) => n.toFixed(2);

  return {
    data: {
      base_amount: money(rupees(quote.baseAmountMinor)),
      payable_amount: money(rupees(quote.totalPayableMinor)),
      customer_rate: quote.customerRate,
      fixed_customer_rate: quote.customerRate,
      settlement_rates: quote.settlementRate ?? "",
      // Selling under cost is allowed; the panel warns rather than blocks.
      at_a_loss: quote.atALoss,
      margin: quote.l1NetMinor === undefined ? null : rupees(quote.l1NetMinor),
      service_partner: "",
      service_partner_mode: quote.settlementRate ? "online" : "",
      discloser: "",
      gst: money(rupees(line("gst")?.amountMinor)),
      tcs: money(rupees(line("tcs")?.amountMinor)),
      tt_charges: rupees(quote.ttChargeMinor),
      tt_charges_with_gst:
        rupees(line("tt_charge")?.amountMinor) +
        rupees(line("tt_charge")?.taxMinor),
      foreign_bank_charges: rupees(quote.foreignBankChargeMinor),
      foreign_bank_charges_with_gst:
        rupees(line("bank_charge")?.amountMinor) +
        rupees(line("bank_charge")?.taxMinor),
      chain: { l1: "", l2: "", l3: "", branch: "" },
      commission: { l1: at(1), l2: at(2), l3: at(3) },
      cumulative_commission: {
        l1: at(1),
        l2: at(1) + at(2),
        l3: at(1) + at(2) + at(3),
      },
      cumulative_commission_without_addon: {
        l1: at(1),
        l2: at(1) + at(2),
        l3: at(1) + at(2) + at(3),
      },
      profit: {
        // L1's actual take, which moves with the customer rate; the
        // commission alone does not. L2 and L3 earn what was agreed.
        l1: money(
          quote.l1NetMinor === undefined
            ? at(1)
            : rupees(quote.l1NetMinor),
        ),
        l2: money(at(2)),
        l3: money(at(3)),
      },
    },
  };
}

/* ----------------------------------------------------------- beneficiary */

export async function postBeneficiaryEntry(
  payload: Payload<Record<string, unknown>>,
): Promise<{ data: unknown }> {
  const body = (payload.data ?? {}) as Record<string, string>;
  const customerId = body.customers_id;
  if (!customerId) throw new Error("A customer is required.");

  // Which routing code a corridor uses depends on the bank's country, and
  // the form makes the right one mandatory: a sort code for the UK, a BSB
  // for Australia, a transit number for Canada, a routing number for the
  // US. Only the US one was being read, so every other corridor collected
  // a mandatory code and then saved the beneficiary without it -- leaving
  // a payee the payment cannot actually be instructed against.
  const routing = [
    { value: body.routing_number, type: "aba" },
    { value: body.SORT || body.short_code, type: "sort" },
    { value: body.BSB || body.bsbcode0, type: "bsb" },
    { value: body.TRANSIT || body.transit_code, type: "transit" },
  ].find((candidate) => (candidate.value ?? "").trim() !== "");

  const { data } = await moneefloApi.post(
    `/customers/${customerId}/beneficiaries`,
    {
      name: body.name,
      nickname: body.Nickname || undefined,
      relation: body.relation || body.beneficiaryType || undefined,
      country: body.country || undefined,
      city: body.city || undefined,
      addressLine1: body.addr_line_1 || undefined,
      bankName: body.bank_name || undefined,
      // The bank's country decides the routing code, so it has to be kept.
      bankCountry: body.bank_country || undefined,
      accountNumber: body.account_number || undefined,
      // The API wants at least 15 characters; a blank field is not an IBAN.
      iban: (body.iban_no ?? "").trim().length >= 15 ? body.iban_no : undefined,
      swiftCode: body.swift_code || undefined,
      routingCode: routing?.value?.trim() || undefined,
      routingCodeType: routing?.type,
    },
  );
  return { data };
}

/* ----------------------------------------------------------------- submit */

/**
 * The wizard submits multipart FormData built for Xano's D1_order_v2. The new
 * endpoint takes JSON and prices the order itself, so the fields are read back
 * out of the form rather than the wizard being changed.
 *
 * Placing an order against a held deal confirms that deal: the order already
 * exists at `quoted`, so this moves it on rather than creating a second one.
 */
export type LegacyCreateOrderResponse = {
  result: { id: string; payable_amount: string };
  order_id?: string;
  /** -1 means "no cap", which is what the screen checks for. */
  remaining_orders: number;
};

export async function postcreateOrder(
  payload: Payload<FormData | Record<string, unknown>>,
): Promise<{ data: LegacyCreateOrderResponse }> {
  const form = payload.data;
  const read = (key: string): string | null =>
    form instanceof FormData
      ? ((form.get(key) as string | null) ?? null)
      : (((form as Record<string, unknown>)?.[key] as string) ?? null);

  const dealId = read("deals_id");
  const beneficiaryId = read("beneficiary2_id");

  if (dealId) {
    // Before advancing the deal, not after: an order that goes through
    // with no payee recorded is worse than one that refuses to.
    if (beneficiaryId) {
      await moneefloApi.post(`/orders/${dealId}/beneficiary`, {
        beneficiaryId,
      });
    }

    // The margins and customer rate the desk set on the wizard are an
    // override for this order, not decoration. Advancing the deal used to
    // be the whole of this function, so those edits were quoted live into
    // the Calculations panel and then dropped: a partner margin typed as 8
    // was placed at the rate card's 0.5. Written before the status moves,
    // because pricing is only editable while the deal is unplaced.
    const customerRate = Number(read("customer_rate"));
    const partnerMargin = Number(read("l2_commission"));
    const settlementRate = Number(read("settlement_rates"));
    const reprice: Record<string, number | string> = {};
    if (customerRate > 0) reprice.customerRate = customerRate;
    if (read("l2_commission") !== null && !Number.isNaN(partnerMargin)) {
      reprice.partnerMargin = partnerMargin;
    }
    if (settlementRate > 0) reprice.settlementRate = settlementRate;
    // And who is actually remitting. A deal is usually booked against a
    // placeholder before anybody has been registered, so the remitter
    // chosen on the wizard's second step is the real one — and it was
    // being dropped with everything else, which is why an order raised
    // for a registered customer still read "Unnamed remitter".
    const remitter = read("customers_id");
    if (remitter) reprice.customerId = remitter;
    if (Object.keys(reprice).length > 0) {
      await moneefloApi.post(`/orders/${dealId}/reprice`, reprice);
    }
    // Raising an order against a deal moves it on, which is what makes the
    // deal utilized. A deal whose settlement rate is already agreed is
    // confirmed, and asking to confirm it again answered "Order is already
    // confirmed" and left the order unplaced.
    const { data: current } = await moneefloApi.get(`/orders/${dealId}`);
    const status = (current as { status?: string })?.status;

    if (status === "quoted" || status === "draft") {
      await moneefloApi.post(`/orders/${dealId}/status`, {
        status: "confirmed",
      });
    }
    const { data } = await moneefloApi.post(`/orders/${dealId}/status`, {
      status: "processing",
    });
    return { data: present(data, dealId) };
  }

  // No deal behind it: price and place one outright.
  const { data } = await moneefloApi.post("/orders", {
    customerId: read("customers_id"),
    purposeId: read("purpose_id") ?? undefined,
    currency: read("currency"),
    fxAmount: Number(read("amount")),
    beneficiaryId: beneficiaryId ?? undefined,
    initialStatus: "quoted",
  });
  return { data: present(data, null) };
}

function present(data: unknown, fallbackId: string | null) {
  const order = data as {
    id?: string;
    orderNo?: string;
    totalPayableMinor?: string;
  };
  return {
    result: {
      id: order.id ?? fallbackId ?? "",
      payable_amount: rupees(order.totalPayableMinor).toFixed(2),
    },
    order_id: order.orderNo,
    // Xano capped how many orders a partner could place. Nothing does now.
    remaining_orders: -1,
  };
}

/* -------------------------------------------------- documents and LRS ---- */

/** Needs object storage; empty until the bucket has a public domain. */
/**
 * The customer's KYC papers: PAN and Aadhaar, which is all a remittance
 * asks for. Everything else they have on file -- offer letters, visas,
 * passbooks -- belongs to an order, not to KYC, and listing those here
 * buried the upload section under seven rows of other people's paperwork.
 */
export async function getKYCDocs(
  payload: Payload<{ customers_id?: string }> & {
    params?: { customers_id?: string };
  },
): Promise<{ data: { customer_kyc: unknown[]; customer_reg_at?: string } }> {
  const customerId =
    payload.params?.customers_id ?? payload.data?.customers_id ?? "";
  if (!customerId) return { data: { customer_kyc: [] } };

  const { data } = await moneefloApi.get(
    `/customers/${encodeURIComponent(customerId)}/kyc-documents`,
  );
  const kinds = (data ?? []) as {
    docType: string;
    label: string;
    last4: string | null;
    onFile: boolean;
    files: { id: string; fileName: string | null; uploadedAt: string; url: string | null }[];
  }[];

  return {
    data: {
      customer_kyc: kinds.map((kind) => ({
        name: kind.last4 ? `${kind.label} ••••${kind.last4}` : kind.label,
        doc_id: kind.docType,
        data: kind.files.map((f) => ({ url: f.url, name: f.fileName })),
      })),
      customer_reg_at: kinds.flatMap((k) => k.files)[0]?.uploadedAt,
    },
  };
}

/**
 * Which documents a purpose needs, in the shape the steps read: they filter
 * on `type === "upload"` and mark anything not `optional` with an asterisk.
 */
export async function getDocumentMapping(
  // Loose on purpose: three screens call this with differently typed
  // param bags, and two spellings of the service partner are in use.
  payload: Payload<Record<string, unknown>> & {
    params?: Record<string, unknown>;
  },
): Promise<{ data: unknown[] }> {
  const params = (payload.params ?? {}) as Record<string, unknown>;
  const body = (payload.data ?? {}) as Record<string, unknown>;
  const purposeId = params.purpose_id ?? body.purpose_id;
  if (!purposeId) return { data: [] };

  const partnerId = params.service_partner_id ?? params.service_provider;

  const { data } = await moneefloApi.get("/catalog/document-requirements", {
    params: {
      purposeId: String(purposeId),
      servicePartnerId: partnerId ? String(partnerId) : undefined,
    },
  });
  const rules = (data ?? []) as {
    id: string;
    docType: string;
    label: string;
    mandatory: boolean;
    xanoDocListId: number | null;
  }[];

  return {
    data: rules.map((r) => ({
      id: r.id,
      doc_name: r.label,
      // Everything here is something to upload; Xano also had text fields
      // on this table and none survive in the migrated rules.
      type: "upload",
      optional: !r.mandatory,
      // The step keys its uploads by this, and the API takes it back as
      // requirementId, so it is our id rather than Xano's.
      doc_list_id: r.id,
    })),
  };
}

/** A data URL, as the wizard stages a picked file, back into bytes. */
function fileFromDataUrl(dataUrl: string, name: string): File {
  const [head, encoded] = dataUrl.split(",");
  const type = /data:([^;]+)/.exec(head)?.[1] ?? "application/octet-stream";
  const binary = atob(encoded ?? "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type });
}

/**
 * The documents collected on the order wizard, filed against the order.
 *
 * The wizard stages each pick as a base64 data URL and hands them over in
 * one go at the end, so they are turned back into files and posted one at a
 * time. Sequentially, not in parallel: twenty passports at once is a way to
 * find out what the upload limit is.
 */
export async function uploadAllDocs(
  payload: Payload<FormData | Record<string, unknown>>,
): Promise<{ data: unknown; status: number }> {
  const form = payload.data;
  if (!(form instanceof FormData)) {
    throw new Error("Documents are uploaded as form data.");
  }
  const orderId = String(form.get("deals_id") ?? "").trim();
  if (!orderId) {
    throw new Error("An order is needed before its documents can be filed.");
  }

  const staged = form.getAll("docs[]").map((entry) => {
    try {
      return JSON.parse(String(entry)) as {
        doc?: string;
        doc_name?: string;
        doc_id?: number | string;
      };
    } catch {
      return null;
    }
  });

  const uploaded: unknown[] = [];
  for (const doc of staged) {
    if (!doc?.doc?.startsWith("data:")) continue;
    const name = (doc.doc_name || "document").replace(/[^\w.\- ]+/g, "_");
    const body = new FormData();
    body.append("file", fileFromDataUrl(doc.doc, `${name}.pdf`));
    body.append("docType", doc.doc_name || "other");
    if (doc.doc_id) body.append("requirementId", String(doc.doc_id));

    const { data } = await moneefloApi.post(
      `/orders/${encodeURIComponent(orderId)}/documents`,
      body,
    );
    uploaded.push(data);
  }

  return { data: uploaded, status: 200 };
}

/** LRS declarations are not migrated yet. */
/**
 * LRS declarations, which the new backend does not hold yet.
 *
 * The shape matters even while it is empty: the step reads
 * `res.data.response.filter(...)`, so handing back a bare array threw a
 * TypeError into a silent catch and the declaration step stopped part-way
 * through. An empty list of the right shape fails cleanly instead.
 */
export async function getDisclose(
  _payload: Payload<unknown>,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
): Promise<{ data: { response: any[]; zenith: any } }> {
  return { data: { response: [], zenith: undefined } };
}

export async function postDisclose(
  _payload: Payload<unknown>,
): Promise<{ data: unknown }> {
  throw new Error("LRS declarations are not available on the new backend yet.");
}

export async function getMoneefloCustomer(
  _payload: Payload<unknown>,
): Promise<{ data: Record<string, never> }> {
  return { data: {} };
}
